import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { PartnerLayout } from "../../components/PartnerLayout";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Warning, MapPin, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = [
  { key: "", label: "All" },
  { key: "pending", label: "To Accept" },
  { key: "scheduled", label: "Scheduled" },
  { key: "picked_up", label: "Picked Up" },
  { key: "completed", label: "Completed" },
];
const fmt = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

export default function PartnerSellPickups() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/partner/sell-orders", { params: { status: tab } }).then((r) => setRows(r.data)).catch(() => {});
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const act = async (oid, action) => {
    setBusy(true);
    try {
      await api.post(`/partner/sell-orders/${oid}/${action}`);
      toast.success(action === "accept" ? "Pickup accepted" : action === "reject" ? "Pickup declined" : "Marked as picked up");
      setDetail(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const actions = (o) => {
    if (o.partner_acceptance === "assigned") return (
      <div className="flex justify-end gap-1">
        <Button size="sm" className="rounded-sm" onClick={(e) => { e.stopPropagation(); act(o.id, "accept"); }} data-testid={`sell-accept-${o.id}`}>Accept</Button>
        <Button size="sm" variant="outline" className="rounded-sm text-destructive" onClick={(e) => { e.stopPropagation(); act(o.id, "reject"); }} data-testid={`sell-reject-${o.id}`}>Reject</Button>
      </div>
    );
    if (o.partner_acceptance === "accepted" && o.status !== "picked_up" && o.status !== "completed") return (
      <Button size="sm" className="rounded-sm bg-green-600 hover:bg-green-700" onClick={(e) => { e.stopPropagation(); act(o.id, "pickup"); }} data-testid={`sell-pickup-${o.id}`}>Mark Picked Up</Button>
    );
    return <span className="text-xs text-muted-foreground">No action</span>;
  };

  return (
    <PartnerLayout>
      <PageHeader title="Sell Pickups" subtitle="Device pickups assigned to you" />
      <div className="p-8 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm" data-testid="sell-pickup-tabs">
            {TABS.map((t) => <TabsTrigger key={t.key} value={t.key} className="rounded-sm" data-testid={`sptab-${t.label.toLowerCase().replace(/ /g, "-")}`}>{t.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <div className="border border-border rounded-sm bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Booking</TableHead><TableHead>Device</TableHead><TableHead>Customer</TableHead>
                <TableHead>Pickup</TableHead><TableHead className="text-right">Offer</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="partner-sell-table">
              {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No pickups</TableCell></TableRow>}
              {rows.map((o) => (
                <TableRow key={o.id} className={`cursor-pointer ${o.partner_acceptance === "assigned" ? "bg-amber-50/60" : "hover:bg-secondary/30"}`} onClick={() => setDetail(o)} data-testid={`sell-row-${o.booking_id}`}>
                  <TableCell className="font-mono font-semibold">{o.booking_id}</TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-1.5">{o.partner_acceptance === "assigned" && <Warning size={14} weight="fill" className="text-amber-500" />}{o.category_name} {o.brand_name}</div>
                    <div className="text-xs text-muted-foreground">{o.model_name}</div>
                  </TableCell>
                  <TableCell>{o.customer?.name}<div className="text-xs text-muted-foreground">{o.customer?.pincode}</div></TableCell>
                  <TableCell className="text-xs">{o.pickup_date}<div className="text-muted-foreground">{o.pickup_slot}</div></TableCell>
                  <TableCell className="text-right font-mono">{fmt(o.final_price ?? o.counter_offer?.amount ?? o.estimated_price)}</TableCell>
                  <TableCell><span className="text-xs font-semibold bg-secondary px-2 py-1 rounded">{o.partner_acceptance === "assigned" ? "to accept" : o.status}</span></TableCell>
                  <TableCell className="text-right">{actions(o)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="partner-sell-detail">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-heading">{detail.booking_id}</SheetTitle></SheetHeader>
              <div className="py-5 space-y-4 text-sm">
                <div className="bg-secondary rounded-sm p-3">
                  <p className="font-semibold">{detail.customer?.name}</p>
                  <p className="text-muted-foreground flex items-center gap-1.5 mt-1"><Phone size={13} /> {detail.customer?.phone}</p>
                  <p className="text-muted-foreground flex items-start gap-1.5 mt-1"><MapPin size={13} className="mt-0.5" /> {detail.customer?.address}, {detail.customer?.city} {detail.customer?.pincode}</p>
                </div>
                <div>
                  <p className="label-caps mb-1">Device</p>
                  <p>{detail.category_name} · {detail.brand_name} · {detail.model_name}</p>
                  <p className="mt-1">Offer to pay: <b className="text-primary">{fmt(detail.final_price ?? detail.counter_offer?.amount ?? detail.estimated_price)}</b></p>
                  <p className="text-xs text-muted-foreground">Pickup: {detail.pickup_date}, {detail.pickup_slot}</p>
                </div>
                <div className="flex gap-2 pt-2">{actions(detail)}</div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </PartnerLayout>
  );
}
